import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CustomColumnType } from '../../../types/lead';
import { CUSTOM_COLUMN_TYPES } from './types';
import { ChoiceOptionsSection } from './ChoiceOptionsSection';

interface CreateColumnTabProps {
  handleCreateColumn: (e: React.FormEvent) => void;
  newColName: string;
  setNewColName: (val: string) => void;
  nameError: string | null;
  setNameError: (val: string | null) => void;
  newColType: CustomColumnType;
  handleTypeChange: (type: CustomColumnType) => void;
  optionsError: string | null;
  setOptionsError: (val: string | null) => void;
  currentOptionInput: string;
  setCurrentOptionInput: (val: string) => void;
  handleAddOption: () => void;
  optionsList: string[];
  handleRemoveOption: (index: number) => void;
  handleOptionRename: (index: number, newLabel: string) => void;
  createOptionErrors?: Record<number, string>;
}

export const CreateColumnTab: React.FC<CreateColumnTabProps> = React.memo(({
  handleCreateColumn,
  newColName,
  setNewColName,
  nameError,
  setNameError,
  newColType,
  handleTypeChange,
  optionsError,
  setOptionsError,
  currentOptionInput,
  setCurrentOptionInput,
  handleAddOption,
  optionsList,
  handleRemoveOption,
  handleOptionRename,
  createOptionErrors = {},
}) => {
  const [colNameWarning, setColNameWarning] = useState<boolean>(false);

  return (
    <Box
      component="form"
      id="create-custom-column-form"
      onSubmit={handleCreateColumn}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      <Typography variant="body2" color="text.secondary">
        Add a new column for your business leads.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          fullWidth
          label="Column Name"
          placeholder="e.g. Company Name, Deal Size, Designation"
          value={newColName}
          onChange={(e) => {
            const val = e.target.value;
            setNewColName(val);
            setColNameWarning(val.length >= 25);
            if (nameError) setNameError(null);
          }}
          onFocus={(e) => setColNameWarning(e.target.value.length >= 25)}
          onBlur={() => setColNameWarning(false)}
          error={Boolean(nameError)}
          inputProps={{ maxLength: 25 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderColor: colNameWarning ? 'warning.main' : undefined,
            },
          }}
        />
        {colNameWarning && !nameError && (
          <Typography variant="caption" sx={{ color: 'warning.main', px: 1, fontSize: '0.75rem', fontWeight: 500 }}>
            Reached maximum 25 characters limit
          </Typography>
        )}
        {nameError && (
          <Typography variant="caption" sx={{ color: 'error.main', px: 1, fontSize: '0.75rem', fontWeight: 500 }}>
            {nameError}
          </Typography>
        )}
      </Box>

      <FormControl fullWidth>
        <InputLabel id="field-type-select-label">Field Data Type</InputLabel>
        <Select
          labelId="field-type-select-label"
          value={newColType}
          label="Field Data Type"
          onChange={(e) => handleTypeChange(e.target.value as CustomColumnType)}
          renderValue={(selected) => {
            const typeConfig = CUSTOM_COLUMN_TYPES.find((t) => t.type === selected);
            if (!typeConfig) return selected;
            const IconComponent = typeConfig.icon;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <IconComponent fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={500}>
                  {typeConfig.label}
                </Typography>
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 320,
                borderRadius: 1.5,
                mt: 0.5,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          {CUSTOM_COLUMN_TYPES.map((t) => {
            const IconComponent = t.icon;
            const isSelected = newColType === t.type;
            return (
              <MenuItem
                key={t.type}
                value={t.type}
                sx={{
                  py: 1,
                  px: 1.5,
                  mx: 0.5,
                  my: 0.25,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: isSelected ? 'primary.50' : 'action.hover',
                    color: isSelected ? 'primary.main' : 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  <IconComponent fontSize="small" />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={isSelected ? 600 : 500} color="text.primary">
                    {t.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {t.description}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {(newColType === 'select' || newColType === 'multi_select') && (
        <ChoiceOptionsSection
          options={optionsList}
          initialCount={0}
          addInput={currentOptionInput}
          onAddInputChange={setCurrentOptionInput}
          onAddOption={handleAddOption}
          addError={optionsError}
          onClearAddError={() => setOptionsError(null)}
          onOptionRename={handleOptionRename}
          onRemoveOption={handleRemoveOption}
          optionErrors={createOptionErrors}
        />
      )}
    </Box>
  );
});

CreateColumnTab.displayName = 'CreateColumnTab';
